import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { Ticket } from '../lib/types';

export function useWorkspaceTicket(id?: string) {
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [message, setMessage] = useState('');
  const [title, setTitle] = useState('');
  const [channel, setChannel] = useState('Email');
  const [tone, setTone] = useState('Friendly');
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(Boolean(id));
  const [error, setError] = useState('');
  const pending = useRef<AbortController | null>(null);

  const load = useCallback((value: Ticket) => {
    setTicket(value); setMessage(value.message); setTitle(value.title);
    setDraft(value.draft); setChannel(value.channel); setTone(value.tone);
  }, []);
  const reset = useCallback(() => {
    setTicket(null); setMessage(''); setTitle(''); setDraft('');
    setChannel('Email'); setTone('Friendly'); setError('');
  }, []);
  const refreshTicket = useCallback(async () => {
    pending.current?.abort();
    const controller = new AbortController();
    pending.current = controller;
    reset();
    if (!id) { setLoading(false); return; }
    setLoading(true);
    try {
      const value = await api<Ticket>(`/tickets/${id}`, 'GET', undefined, controller.signal);
      if (!controller.signal.aborted) load(value);
    } catch (error: any) {
      if (!controller.signal.aborted) setError(error.message);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [id, load, reset]);

  useEffect(() => {
    void refreshTicket();
    return () => pending.current?.abort();
  }, [refreshTicket]);

  return {ticket, setTicket, message, setMessage, title, setTitle, channel, setChannel,
    tone, setTone, draft, setDraft, loading, error, load, reset, refreshTicket};
}

export type WorkspaceTicketState = ReturnType<typeof useWorkspaceTicket>;